import React, { useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faUsers,
  faUser,
  faEdit,
  faSave,
  faTimes,
  faChevronDown,
  faChevronRight,
} from "@fortawesome/free-solid-svg-icons";

// SECURITY: This component uses groupName as the unique identifier
// instead of groupId, which is no longer exposed from the backend.
function AllGroupsSection({ groups, onUserAssignmentClick, onGroupUpdate }) {
  const [expandedGroups, setExpandedGroups] = useState(new Set());
  const [editingGroups, setEditingGroups] = useState(new Set());
  const [editData, setEditData] = useState({});
  const [loading, setLoading] = useState(new Set());

  // SECURITY: Use groupName as identifier instead of groupId
  const toggleGroupExpansion = (groupName) => {
    const newExpanded = new Set(expandedGroups);
    if (newExpanded.has(groupName)) {
      newExpanded.delete(groupName);
      // Also close editing if expanded group is being collapsed
      const newEditing = new Set(editingGroups);
      newEditing.delete(groupName);
      setEditingGroups(newEditing);
    } else {
      newExpanded.add(groupName);
    }
    setExpandedGroups(newExpanded);
  };

  const startEditing = (group) => {
    const groupName = group.groupName || group.name;
    const newEditing = new Set(editingGroups);
    newEditing.add(groupName);
    setEditingGroups(newEditing);

    setEditData({
      ...editData,
      [groupName]: {
        name: group.name || group.groupName,
        description: group.description || "",
      },
    });
  };

  const cancelEditing = (groupName) => {
    const newEditing = new Set(editingGroups);
    newEditing.delete(groupName);
    setEditingGroups(newEditing);

    // Remove edit data for this group
    const newEditData = { ...editData };
    delete newEditData[groupName];
    setEditData(newEditData);
  };

  const handleInputChange = (groupName, field, value) => {
    setEditData({
      ...editData,
      [groupName]: {
        ...editData[groupName],
        [field]: value,
      },
    });
  };

  const saveChanges = async (group) => {
    const groupName = group.groupName || group.name; // Use groupName as identifier
    const newLoading = new Set(loading);
    newLoading.add(groupName);
    setLoading(newLoading);

    try {
      // SECURITY: Use groupName as identifier, not groupId
      const updateData = {
        name: editData[groupName].name,
        description: editData[groupName].description,
      };

      console.log(
        "AllGroupsSection: Sending update data with groupName:",
        groupName,
        updateData,
      );

      if (onGroupUpdate) {
        // SECURITY: Pass groupName as identifier instead of groupId
        await onGroupUpdate(groupName, updateData);
      }

      // Exit editing mode
      const newEditing = new Set(editingGroups);
      newEditing.delete(groupName);
      setEditingGroups(newEditing);

      // Remove edit data for this group
      const newEditData = { ...editData };
      delete newEditData[groupName];
      setEditData(newEditData);
    } catch (error) {
      console.error("Error updating group:", error);
    } finally {
      const newLoading = new Set(loading);
      newLoading.delete(groupName);
      setLoading(newLoading);
    }
  };

  return (
    <div className="common-form-group">
      <h4>All Groups ({groups.length})</h4>
      <div className="users-container">
        {groups.map((group) => {
          // SECURITY: Use groupName as identifier instead of groupId
          const groupName = group.groupName || group.name;
          const isExpanded = expandedGroups.has(groupName);
          const isEditing = editingGroups.has(groupName);
          const isLoading = loading.has(groupName);
          const currentEditData = editData[groupName];

          return (
            <div
              key={`${groupName}-${group.orgName}`}
              className="info-item group-summary-card"
            >
              <div className="group-summary-header">
                <div>
                  <div
                    className="group-summary-title clickable"
                    onClick={() => toggleGroupExpansion(groupName)}
                    style={{ cursor: "pointer" }}
                  >
                    <FontAwesomeIcon
                      icon={isExpanded ? faChevronDown : faChevronRight}
                      className="expand-icon"
                      style={{ marginRight: "8px" }}
                    />
                    <FontAwesomeIcon icon={faUsers} />
                    {group.name || group.groupName}
                  </div>
                  <div className="group-summary-org">
                    <strong>Organization:</strong> {group.orgName}
                  </div>
                  <div className="group-summary-description">
                    <strong>Description:</strong>{" "}
                    {group.description || "No description"}
                  </div>
                </div>
                <div className="group-summary-stats">
                  <div className="group-summary-user-count">
                    {group.userCount || 0} users
                  </div>
                  {/* SECURITY: Removed display of groupId */}
                </div>
              </div>

              {/* Expanded section for editing and user details */}
              {isExpanded && (
                <div className="group-details-section">
                  <div className="group-edit-section">
                    <div className="group-edit-header">
                      <h5>Group Details</h5>
                      {!isEditing ? (
                        <button
                          className="common-button secondary small"
                          onClick={() => startEditing(group)}
                          title="Edit Group"
                        >
                          <FontAwesomeIcon icon={faEdit} />
                          Edit
                        </button>
                      ) : (
                        <div className="common-button-group">
                          <button
                            className="common-button small"
                            onClick={() => saveChanges(group)}
                            disabled={isLoading}
                            title="Save Changes"
                          >
                            <FontAwesomeIcon icon={faSave} />
                            {isLoading ? "Saving..." : "Save"}
                          </button>
                          <button
                            className="common-button secondary small"
                            onClick={() => cancelEditing(groupName)}
                            disabled={isLoading}
                            title="Cancel"
                          >
                            <FontAwesomeIcon icon={faTimes} />
                            Cancel
                          </button>
                        </div>
                      )}
                    </div>

                    {isEditing ? (
                      <div className="group-edit-form">
                        <div className="common-form-field">
                          <label>Group Name:</label>
                          <input
                            type="text"
                            value={currentEditData?.name || ""}
                            onChange={(e) =>
                              handleInputChange(
                                groupName,
                                "name",
                                e.target.value,
                              )
                            }
                            disabled={isLoading}
                          />
                        </div>
                        <div className="common-form-field">
                          <label>Description:</label>
                          <textarea
                            value={currentEditData?.description || ""}
                            onChange={(e) =>
                              handleInputChange(
                                groupName,
                                "description",
                                e.target.value,
                              )
                            }
                            rows="3"
                            disabled={isLoading}
                          />
                        </div>
                      </div>
                    ) : (
                      <div className="group-info-display">
                        <div className="info-row">
                          <strong>Name:</strong> {group.name || group.groupName}
                        </div>
                        <div className="info-row">
                          <strong>Description:</strong>{" "}
                          {group.description || "No description"}
                        </div>
                        <div className="info-row">
                          <strong>Organization:</strong> {group.orgName}
                        </div>
                        <div className="info-row">
                          <strong>User Count:</strong> {group.userCount || 0}
                        </div>
                      </div>
                    )}
                  </div>

                  {group.users && group.users.length > 0 && (
                    <div className="group-users-section">
                      <div className="group-users-title">
                        Users in this group:
                      </div>
                      <div className="group-users-grid">
                        {group.users.map((user) => (
                          <div key={user.uuid} className="group-user-item">
                            <FontAwesomeIcon icon={faUser} />
                            {user.firstName} {user.lastName} ({user.email})
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default AllGroupsSection;
